/* eslint-disable @typescript-eslint/no-explicit-any */
import { toasts } from '@nexans/react-ui-kit'
import { DefaultOptions, QueryClient, UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios'

import { Dto } from '@/types'

import { i18next } from './react-i18next'

const queryConfig: DefaultOptions = {
  queries: {
    throwOnError: false,
    refetchOnWindowFocus: false,
    retry: false,
  },
}

export const queryClient = new QueryClient({ defaultOptions: queryConfig })

export type ExtractFnReturnType<FnType extends (...args: any) => any> = Awaited<ReturnType<FnType>>

export type QueryConfig<QueryFnType extends (...args: any) => any> = Omit<
  UseQueryOptions<ExtractFnReturnType<QueryFnType>>,
  'queryKey' | 'queryFn'
>

export type InfiniteQueryConfig<QueryFnType extends (...args: any) => any> = Omit<
  UseInfiniteQueryOptions<ExtractFnReturnType<QueryFnType>>,
  'queryKey' | 'queryFn'
>

export type MutationConfig<MutationFnType extends (...args: any) => any> = UseMutationOptions<
  ExtractFnReturnType<MutationFnType>,
  AxiosError,
  Parameters<MutationFnType>[0]
>

export function getQueryParamsFrom(data?: any): string {
  if (!data) return ''
  const params = new URLSearchParams()

  for (const key in data) {
    if (data[key] instanceof Date) {
      params.append(key, data[key].toISOString())
    } else if (typeof data[key] === 'object') {
      for (const subKey in data[key]) {
        if (data[key][subKey] !== '' && data[key][subKey] !== undefined) {
          params.append(key, data[key][subKey])
        }
      }
    } else if (data[key] !== undefined && data[key] !== '') {
      params.append(key, data[key])
    }
  }

  return `?${params.toString()}`
}

export function getFileNameFromHeader(r: AxiosResponse): string | undefined {
  let filename: string | undefined = undefined
  let disposition: string | undefined
  const headers = r.headers
  if (headers instanceof AxiosHeaders) {
    disposition = headers.get('Content-Disposition')?.toString()
  }
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(disposition)
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '')
    }
  }
  return filename
}

export function handleApiError(error: AxiosError<Dto['ExceptionDetails']>): void {
  // 5xx are already handled globally by the axios response interceptor
  const status = error.response?.status
  if (status && status >= 500 && status < 600) return

  const t = i18next.t
  if (error.response?.data.errors?.length) {
    error.response.data.errors.forEach((err) => {
      toasts.error(t('common:toast.error'), t(`errors:${err.errorCode}`))
    })
  } else if (error.response?.data.message) {
    toasts.error(t('common:toast.error'), t(`errors:${error.response.data.message}`))
  } else {
    toasts.error(t('common:toast.error'), t('common:errors.unknown'))
  }
}
